/**
 * スコア履歴を localStorage に保存する。
 *
 * バックエンドを使わず、ブラウザ内完結。
 * 保存件数には上限を設け、古いものから自動削除する。
 */

import { LEGACY_STORAGE, scoreHistoryStorageKey } from '../game/config/gameConfig';
import { detectStorage, type StorageLike } from '../game/systems/StorageSystem';

export interface ScoreRecord {
  /** ISO 8601 形式の日時 */
  date: string;
  score: number;
  highScore: number;
  isNewRecord: boolean;
  maxLevelReached: number;
  maxItemName: string;
  stageTitle: string;
}

const MAX_HISTORY = 50;

/** 同一タブ内で一時的に localStorage を無効化された場合のフォールバック。 */
function createMemoryStorage(): StorageLike {
  const data = new Map<string, string>();
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => data.set(key, value),
    removeItem: (key) => data.delete(key),
  };
}

export class ScoreHistory {
  private readonly storage: StorageLike | null;
  private readonly key: string;
  private readonly legacyKey?: string;
  private records: ScoreRecord[] = [];

  constructor(
    stageId: string,
    storage: StorageLike | null = detectStorage() ?? createMemoryStorage(),
  ) {
    this.storage = storage;
    this.key = scoreHistoryStorageKey(stageId);
    // 福岡単独だった頃の履歴は、福岡ステージを開いたときだけ引き継ぐ
    this.legacyKey =
      stageId === LEGACY_STORAGE.stageId ? LEGACY_STORAGE.scoreHistory : undefined;
    this.records = this.read();
  }

  /** 履歴を追加する。 */
  add(record: Omit<ScoreRecord, 'date'>): ScoreRecord {
    const entry: ScoreRecord = { ...record, date: new Date().toISOString() };
    this.records.unshift(entry);
    if (this.records.length > MAX_HISTORY) {
      this.records = this.records.slice(0, MAX_HISTORY);
    }
    this.persist();
    return entry;
  }

  /** 新着順で全履歴を取得する。 */
  getAll(): ReadonlyArray<ScoreRecord> {
    return [...this.records];
  }

  /** 履歴をクリアする。 */
  clear(): void {
    this.records = [];
    this.persist();
  }

  /** 最高スコア記録を取得する。 */
  best(): ScoreRecord | undefined {
    return this.records.reduce<ScoreRecord | undefined>((best, current) => {
      if (!best || current.score > best.score) return current;
      return best;
    }, undefined);
  }

  private read(): ScoreRecord[] {
    if (!this.storage) return [];
    try {
      let raw = this.storage.getItem(this.key);
      // 旧キーからの移行（旧キーは消さずに残す）
      if (!raw && this.legacyKey) {
        raw = this.storage.getItem(this.legacyKey);
        if (raw) this.storage.setItem(this.key, raw);
      }
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isScoreRecord);
    } catch {
      return [];
    }
  }

  private persist(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.key, JSON.stringify(this.records));
    } catch {
      // localStorage が使えない環境では保存を諦める
    }
  }
}

function isScoreRecord(value: unknown): value is ScoreRecord {
  if (!value || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.date === 'string' &&
    typeof r.score === 'number' &&
    typeof r.highScore === 'number' &&
    typeof r.isNewRecord === 'boolean' &&
    typeof r.maxLevelReached === 'number' &&
    typeof r.maxItemName === 'string' &&
    typeof r.stageTitle === 'string'
  );
}
